import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  useTable,
  usePagination,
  useSortBy,
  useGlobalFilter,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {

  faCancel,
  faCheckCircle,
  faEdit,
  faEllipsisV,
  faEye,
  faPencil,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";

/* ------------------------ SEARCH ------------------------ */
const GlobalFilter = ({ filter, setFilter }) => (
  <input
    value={filter || ""}
    onChange={(e) => setFilter(e.target.value)}
    placeholder="Search..."
    className="form-control"
  />
);

/* ------------------------ TABLE ------------------------ */
const TableComponent = ({
  columns,
  data,
  globalFilter,
  setGlobalFilter,
  pageSize,
  setPageSize,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    previousPage,
    nextPage,
    setGlobalFilter: tableSetFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize, globalFilter },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Sync external search to table
  useEffect(() => {
    tableSetFilter(globalFilter);
  }, [globalFilter]);

  const { pageIndex } = state;

  return (
    <>
      <table
        className="table table-striped"
        {...getTableProps()}
        style={{ fontSize: "14px" }}
      >
        <thead>
          {headerGroups.map((hg) => (
            <tr {...hg.getHeaderGroupProps()}>
              {hg.headers.map((col) => (
                <th
                  {...col.getHeaderProps(col.getSortByToggleProps())}
                  style={{
                    cursor: "pointer",
                    width: col.width,
                    minWidth: col.minWidth,
                    maxWidth: col.maxWidth,
                  }}
                >
                  {col.render("Header")}
                  {col.isSorted ? (col.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);

            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    style={{
                      color:
                        row.original.status === "Pending"
                          ? "#0d6efd" // blue
                          : row.original.status === "Approved"
                            ? "#198754" // green
                            : "inherit",
                    }}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center">
        <button
          className="btn btn-secondary"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          Previous
        </button>

        <span>
          Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
        </span>

        <button
          className="btn btn-secondary"
          onClick={nextPage}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
    </>
  );
};

/* ------------------------ MAIN COMPONENT ------------------------ */
const Additions = ({ accessOfDepartments, permissions }) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState(null);

  const [pageSize, setPageSize] = useState(25);
  const [globalFilter, setGlobalFilter] = useState("");

  const navigate = useNavigate();
  const name = localStorage.getItem("name");
  const badgeId = localStorage.getItem("badgeId");
  const pin = localStorage.getItem("pin");
  const code = localStorage.getItem("code");
  const token = localStorage.getItem("token");

  /* ---------- VIEW ---------- */
  const handleView = (row) => {
    navigate("../../previewInventory", { state: { id: row.id } });
  };

  /* ---------- EDIT ---------- */
  const handleEdit = (row) => {
    navigate("../addInventory", { state: { rowId: row.id } });
  };
  const handleCancel = async (rowData) => {
    Swal.fire({
      title: "Confirm Cancellation",
      text: "Enter your PIN to cancel this voucher. This action cannot be undone.",
      icon: "warning",
      input: "password",
      inputPlaceholder: "Enter PIN",
      inputAttributes: {
        maxlength: 6,
        autocapitalize: "off",
        autocorrect: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Cancel Voucher",
      preConfirm: async (enteredPin) => {
        if (!enteredPin) {
          Swal.showValidationMessage("PIN is required");
          return false;
        }

        if (enteredPin !== pin) {
          Swal.showValidationMessage("Invalid PIN");
          return false;
        }

        try {
          await apiClient.post(
            `${SERVER_BASE}/cancelInvnetoryDraft/${rowData.id}`,
            {
              cancelledBy: name, // ✅ Name added
              type: 'Inventory'
            }
          );

          return true; // ✅ tells Swal it's successful
        } catch (err) {
          Swal.showValidationMessage("Failed to cancel voucher");
          return false;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: "success",
          title: "Cancelled",
          text: "Voucher cancelled successfully.",
        });

        loadData();
      }
    });
  };
  /* ---------- FETCH DATA ---------- */
  const loadData = async () => {
    setLoading(true);

    try {
      if (statusFilter === "pending") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getPendingAddDrafts",
          { params: { code, accessOfDepartments } }
        );
        setTableData(data);
      } else if (statusFilter === "approved") {
        const { data } = await apiClient.get(
          SERVER_BASE + "/getApprovedAddDrafts",
          { params: { code, accessOfDepartments } }
        );
        setTableData(data);
      } else {
        const [p, a] = await Promise.all([
          apiClient.get(SERVER_BASE + "/getPendingAddDrafts", {
            params: { code, accessOfDepartments },
          }),
          apiClient.get(SERVER_BASE + "/getApprovedAddDrafts", {
            params: { code, accessOfDepartments },
          }),
        ]);

        setTableData([...p.data, ...a.data]);
      }
    } catch (err) {
      console.error("Error loading additions:", err);
    }

    setLoading(false);
  };
  const departmentOptions = useMemo(() => {
    const departments = [...new Set(tableData.map(d => d.department).filter(Boolean))];
    return [
      { value: "all", label: "All Departments" },
      ...departments.map(dep => ({ value: dep, label: dep })),
    ];
  }, [tableData]);
  const filteredData = useMemo(() => {
    if (!departmentFilter || departmentFilter.value === "all") {
      return tableData;
    }

    return tableData.filter(
      row => row.department === departmentFilter.value
    );
  }, [tableData, departmentFilter]);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  /* ---------- Status Options ---------- */
  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "all", label: "All" },
  ];

  /* ---------- TABLE COLUMNS ---------- */
  const columns = useMemo(
    () => [
      { Header: "S.No", Cell: ({ row }) => row.index + 1 },

      {
        Header: "Date",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        accessor: "date",
        Cell: ({ value }) => (value ? moment(value).format("DD-MMM-YY") : ""),
      },

      { Header: "Ref No.", accessor: "draftCode" },
      { Header: "Department", accessor: "department" },
      { Header: "Event", accessor: "purpose" },

      {
        Header: "Inventory Items",
        width: 280,
        minWidth: 280,
        maxWidth: 280,
        accessor: "inventory_data",
        Cell: ({ value }) => {
          try {
            const parsed =
              typeof value === "string" ? JSON.parse(value) : value;
            return parsed?.map((i) => i.item).join(", ");
          } catch {
            return "-";
          }
        },
      },

      { Header: "Status", accessor: "status" },
      {
        Header: "Preview",
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        Cell: ({ row }) => {
          const r = row.original;

          return (
            permissions?.read && (
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() => handleView(r)}
              >
                View
              </button>
            )
          );
        },
      },
      {
        Header: "#",
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        Cell: ({ row }) => {
          const r = row.original;

          return (
            <div className="dropdown">
              <button
                className="btn btn-light btn-sm"
                data-bs-toggle="dropdown"
                id={`drop-${row.index}`}
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul className="dropdown-menu dropdown-menu-end">
                {/*<li>
                  <button className="dropdown-item" onClick={() => handleView(r)}>
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </button>
                </li> */}
                {r.status == "Pending" && permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEdit(r)}
                    >
                      <FontAwesomeIcon icon={faPencil} /> Edit
                    </button>
                  </li>
                )}
                {permissions?.delete &&
                  r.status == "Pending"
                  && (
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() =>
                          handleCancel(r)
                        }
                      >
                        <FontAwesomeIcon icon={faCancel} />{" "}
                        Cancel
                      </button>
                    </li>
                  )}
                <li>
                  <hr />
                </li>
                <li>
                  {r.createdBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPlus} /> {r.createdBy}
                      {r.created_at &&
                        ` (${moment(r.created_at).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.updatedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faPencil} /> {r.updatedBy}
                      {r.updatedAt &&
                        ` (${moment(r.updatedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
                <li>
                  {r.approvedBy && (
                    <li className="dropdown-item">
                      <FontAwesomeIcon icon={faCheckCircle
                      } /> {r.approvedBy}
                      {r.approvedAt &&
                        ` (${moment(r.approvedAt).format("DD-MMM-YY hh:mm A")})`}
                    </li>
                  )}
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  return (
    <div className="formcontainer mt-5">
      <h4>Additions</h4>

      <div className="row align-items-center mb-3">
        {permissions?.write && (
          <div className="col-md-2 mb-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={() => navigate("../addInventory")}
            >
              Add Inventory
            </button>
          </div>
        )}

        <div className="col-md-4 mb-2">
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
        </div>

        <div className="col-md-2 mb-2">
          <Select
            options={statusOptions}
            value={statusOptions.find((v) => v.value === statusFilter)}
            onChange={(v) => setStatusFilter(v.value)}
          />
        </div>
        <div className="col-md-2 mb-2">
          <Select
            options={departmentOptions}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            placeholder="Filter by Department"
            isClearable
          />
        </div>

        <div className="col-md-2 mb-2">
          <select
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[25, 50, 100, 200].map((s) => (
              <option key={s} value={s}>
                Show {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <TableComponent
          columns={columns}
          data={filteredData}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};

export default Additions;
